import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const hpField = formData.get('website_alt') as string;
    if (hpField && hpField.trim() !== '') {
      setStatus('success');
      setStatusMessage('感谢订阅！');
      setEmail('');
      return;
    }

    try {
      const resp = await fetch(formEl.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const respText = await resp.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(respText); } catch { /* ignore */ }

      if (resp.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setStatus('success');
        setStatusMessage('感谢订阅！');
        setEmail('');
      } else {
        const msg = (parsed as Record<string, unknown>)?.meta as Record<string, unknown> | undefined;
        setStatus('error');
        setStatusMessage((msg?.message as string) || '订阅失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络错误，请稍后重试');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="px-4 md:px-6 py-14 md:py-16 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block cursor-pointer">
              <span className="text-2xl font-heading font-bold text-accent-400">吃瓜网</span>
            </Link>
            <p className="mt-4 text-sm text-foreground-300 leading-relaxed">
              {t('footerTagline')}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-100 mb-4">
              {t('footerQuickLinks')}
            </h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-sm text-foreground-300 hover:text-accent-400 transition-colors cursor-pointer">{t('navHome')}</Link></li>
              <li><Link to="/about" className="text-sm text-foreground-300 hover:text-accent-400 transition-colors cursor-pointer">{t('navAbout')}</Link></li>
              <li><span className="text-sm text-foreground-300 cursor-pointer hover:text-accent-400 transition-colors">{t('footerPrivacy')}</span></li>
              <li><span className="text-sm text-foreground-300 cursor-pointer hover:text-accent-400 transition-colors">{t('footerTerms')}</span></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-100 mb-4">
              {t('footerCategories')}
            </h4>
            <ul className="space-y-2.5">
              <li><Link to="/category/star-scandal" className="text-sm text-foreground-300 hover:text-accent-400 transition-colors cursor-pointer">{t('navStarScandal')}</Link></li>
              <li><Link to="/category/influencer-drama" className="text-sm text-foreground-300 hover:text-accent-400 transition-colors cursor-pointer">{t('navInfluencer')}</Link></li>
              <li><Link to="/category/daily-competition" className="text-sm text-foreground-300 hover:text-accent-400 transition-colors cursor-pointer">{t('navCompetition')}</Link></li>
              <li><Link to="/category/exclusive" className="text-sm text-foreground-300 hover:text-accent-400 transition-colors cursor-pointer">{t('categoryExclusive')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-100 mb-4">
              {t('footerSubscribe')}
            </h4>
            <form
              onSubmit={handleSubscribe}
              action="https://readdy.ai/api/form/d95n3p6a5sk3luk4pgkg"
              method="POST"
              data-readdy-form="newsletter"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-hp"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footerSubscribePlaceholder')}
                  required
                  className="flex-1 px-4 py-2.5 text-sm bg-foreground-900 border border-foreground-700 rounded-lg text-background-100 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="whitespace-nowrap px-5 py-2.5 bg-accent-500 text-background-50 text-sm font-medium rounded-lg hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {status === 'loading' ? '发送中...' : t('footerSubscribeButton')}
                </button>
              </div>
              {status === 'success' && (
                <p className="mt-2 text-xs text-accent-400">{statusMessage}</p>
              )}
              {status === 'error' && (
                <p className="mt-2 text-xs text-red-400">{statusMessage}</p>
              )}
            </form>

            <div className="mt-6 flex gap-4">
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-foreground-300 hover:text-accent-400 hover:bg-foreground-700 transition-colors cursor-pointer" aria-label="微博">
                <i className="ri-weibo-line text-lg" />
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-foreground-300 hover:text-accent-400 hover:bg-foreground-700 transition-colors cursor-pointer" aria-label="微信">
                <i className="ri-wechat-line text-lg" />
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-foreground-300 hover:text-accent-400 hover:bg-foreground-700 transition-colors cursor-pointer" aria-label="抖音">
                <i className="ri-tiktok-line text-lg" />
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-foreground-300 hover:text-accent-400 hover:bg-foreground-700 transition-colors cursor-pointer" aria-label="B站">
                <i className="ri-bilibili-line text-lg" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">{t('copyright')}</p>
          <div className="flex items-center gap-4 text-xs text-foreground-400">
            <span className="cursor-pointer hover:text-accent-400 transition-colors">{t('footerPrivacy')}</span>
            <span className="cursor-pointer hover:text-accent-400 transition-colors">{t('footerTerms')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}